import { z } from "zod";

/* Ausgaben und Eingangsbelege sowie die Kennzahlen für das Dashboard. Beträge in ganzen Cent. */

export const EXPENSE_CATEGORIES = [
  ["material", "Material und Waren"],
  ["fremdleistung", "Fremdleistungen und Subunternehmer"],
  ["fahrzeug", "Fahrzeug und Kraftstoff"],
  ["werkzeug", "Werkzeug und Geräte"],
  ["buero", "Büro und Software"],
  ["telefon", "Telefon und Internet"],
  ["versicherung", "Versicherungen und Beiträge"],
  ["miete", "Miete und Nebenkosten"],
  ["fortbildung", "Fortbildung und Fachliteratur"],
  ["reise", "Reisekosten"],
  ["bewirtung", "Bewirtung und Geschenke"],
  ["beratung", "Steuerberatung und Gebühren"],
  ["sonstiges", "Sonstiges"],
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number][0];
export const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(EXPENSE_CATEGORIES);

export const RECEIPT_TYPES: Record<string, string> = { "application/pdf": "pdf", "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };
export const RECEIPT_MAX_MB = 15;

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum angeben.");
const opt = (max: number) =>
  z
    .string()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v?.trim() ? v.trim() : null));

export const expenseSchema = z.object({
  expenseDate: date,
  supplier: z.string().trim().min(1, "Bitte geben Sie an, von wem der Beleg ist.").max(160),
  receiptNumber: opt(80),
  amountCents: z.number().int().min(1, "Bitte tragen Sie den Betrag ein.").max(1_000_000_000_00),
  vatRate: z.union([z.literal(0), z.literal(7), z.literal(19)]),
  category: z.string().refine((c) => c in CATEGORY_LABEL, "Bitte wählen Sie eine Kategorie."),
  purpose: opt(300),
  projectId: z.uuid().nullable().optional(),
  paidOn: date.nullable().optional(),
});
export type ExpenseInput = z.infer<typeof expenseSchema>;

export interface ExpenseInfo {
  id: string;
  expenseDate: string;
  supplier: string;
  receiptNumber: string | null;
  amountCents: number;
  netCents: number;
  vatCents: number;
  vatRate: number;
  category: string;
  purpose: string | null;
  projectId: string | null;
  paidOn: string | null;
  hasReceipt: boolean;
  receiptName: string | null;
  booked: boolean;
  createdAt: string;
}

export interface ExpenseListResponse {
  items: ExpenseInfo[];
  missingReceipts: number;
  totalCents: number;
}

/** Netto und Umsatzsteuer aus dem Bruttobetrag. */
export function splitGross(grossCents: number, vatRate: number): { netCents: number; vatCents: number } {
  const net = Math.round(grossCents / (1 + vatRate / 100));
  return { netCents: net, vatCents: grossCents - net };
}

export interface DashboardData {
  year: number;
  smallBusiness: boolean;
  /** Rechnungen des Jahres minus Gutschriften (netto). */
  revenueCents: number;
  /** Eingegangene Zahlungen im Jahr (netto, anteilig aus den Rechnungen). */
  incomeCents: number;
  /** Ausgaben des Jahres: netto, als Kleinunternehmer brutto. */
  expensesCents: number;
  profitCents: number;
  openCents: number;
  overdueCents: number;
  overdueCount: number;
  missingReceipts: number;
  draftCount: number;
  months: { month: number; revenueCents: number; incomeCents: number; expensesCents: number }[];
  topCustomers: { name: string; revenueCents: number }[];
  recentPayments: { date: string; amountCents: number; invoiceId: string; invoiceNumber: string; customerName: string }[];
  /** Nur für Kleinunternehmer: Umsatz Vorjahr und laufendes Jahr gegen die Grenzen von 25.000 und 100.000 Euro. */
  limits: { previousYearCents: number; previousLimitCents: number; currentLimitCents: number } | null;
}
